//#region IMPORTS
import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { refreshInstoreOrdersAsync } from "../../features/instoreOrders/instoreOrdersSlice";
import { openModal } from "../../features/dashBoard/dashboardSlice";
import { useAppDispatch } from "../../store/configureStore";
import { IMPORT_TRACK_AND_TRACE_MODAL } from "../../models/_consts";
import { InstoreOrder } from "../../models/instoreOrder";
import { toBackendDate } from "../../helpers/toBackendDate";
import {
  CheckedTrackAndTraceRow,
  TRACK_AND_TRACE_STATUS_LABEL,
  TrackAndTraceRowStatus,
  checkTrackAndTraceRows,
  downloadTrackAndTraceTemplate,
  parseTrackAndTraceFile,
  splitTrackAndTrace,
} from "../../helpers/trackAndTraceImport";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import classNames from "../../helpers/classNames";
import MainModal from "../_layout/MainModal";
import agent from "../../api/agent";
//#endregion

// "Import track & trace" (filter bar of the instore orders, picker/admin):
// a small wizard in one modal — pick a file → check (duplicates vs what is
// already on the order, unknown orders, bad links) → confirm → progress →
// summary. Parsing and the check live in helpers/trackAndTraceImport.
//
// There is no bulk endpoint for instore orders, so saving is a client loop,
// 5 orders at a time: fetch the order FRESH, add the new links to whatever it
// has right now, PUT a partial update (same minimal payload as the bulk status
// actions). Order status is not touched. The modal can't be closed while it
// checks or saves; Stop finishes the batch in flight and skips the rest.
const BATCH = 5;

type Step = "pick" | "checking" | "review" | "saving" | "done";

const STATUS_CLASS: Record<TrackAndTraceRowStatus, string> = {
  new: "text-emerald-700",
  present: "text-gray-500",
  duplicate: "text-gray-500",
  notFound: "text-red-600",
  skippedStatus: "text-amber-600",
  invalidLink: "text-red-600",
  missingCode: "text-red-600",
  lookupFailed: "text-red-600",
};

export default function InstoreOrdersTrackAndTraceImport() {
  //#region SETUP
  const dispatch = useAppDispatch();
  const fileInput = useRef<HTMLInputElement>(null);
  const stopRequested = useRef(false);

  const [step, setStep] = useState<Step>("pick");
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState<CheckedTrackAndTraceRow[]>([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [failedCodes, setFailedCodes] = useState<string[]>([]);
  const [stopped, setStopped] = useState(false);

  const busy = step === "checking" || step === "saving";

  // New links per order, in file order.
  const plan = Array.from(
    rows
      .filter((row) => row.status === "new" && row.order)
      .reduce((map, row) => {
        const entry = map.get(row.order!.id) ?? {
          order: row.order!,
          links: [] as string[],
        };
        entry.links.push(row.link);
        return map.set(row.order!.id, entry);
      }, new Map<number, { order: InstoreOrder; links: string[] }>())
      .values(),
  );
  const newLinkCount = plan.reduce((sum, p) => sum + p.links.length, 0);
  const counts = rows.reduce(
    (acc, row) => ({ ...acc, [row.status]: (acc[row.status] ?? 0) + 1 }),
    {} as Partial<Record<TrackAndTraceRowStatus, number>>,
  );
  //#endregion

  //#region LOGIC
  const open = () => dispatch(openModal(IMPORT_TRACK_AND_TRACE_MODAL));
  const close = () => {
    if (busy) return;
    dispatch(openModal(false));
    if (step === "done") reset();
  };
  const reset = () => {
    setStep("pick");
    setFileName("");
    setRows([]);
    setProgress({ done: 0, total: 0 });
    setFailedCodes([]);
    setStopped(false);
  };

  // Look an order up by its exact code. The list filter is a LIKE, so pick
  // the exact match; archived orders are included so they can be flagged.
  const lookup = async (code: string): Promise<InstoreOrder | null> => {
    const params = new URLSearchParams();
    params.append("order_code", code);
    params.append("include_archived", "1");
    params.append("limit", "20");
    const { data } = await agent.InstoreOrders.list(params);
    return (
      (data as InstoreOrder[]).find((order) => order.order_code === code) ??
      null
    );
  };

  const pickFile = async (file: File | undefined) => {
    if (!file) return;
    setFileName(file.name);
    let parsed;
    try {
      parsed = await parseTrackAndTraceFile(file);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : `${error}`);
      return;
    }
    if (parsed.length === 0) {
      toast.error("No rows found in the file.");
      return;
    }

    const codes = Array.from(
      new Set(parsed.map((row) => row.orderCode).filter(Boolean)),
    );
    setStep("checking");
    setProgress({ done: 0, total: codes.length });
    const orders = new Map<string, InstoreOrder | null | undefined>();
    for (let i = 0; i < codes.length; i += BATCH) {
      await Promise.all(
        codes.slice(i, i + BATCH).map(async (code) => {
          try {
            orders.set(code, await lookup(code));
          } catch {
            orders.set(code, undefined);
          }
          setProgress((p) => ({ ...p, done: p.done + 1 }));
        }),
      );
    }
    setRows(checkTrackAndTraceRows(parsed, orders));
    setStep("review");
  };

  const save = async () => {
    stopRequested.current = false;
    setStopped(false);
    setFailedCodes([]);
    setStep("saving");
    setProgress({ done: 0, total: plan.length });
    const failed: string[] = [];
    let updated = 0;

    // One order: true when saved (or nothing left to add), false on failure.
    const saveOne = async ({
      order,
      links,
    }: {
      order: InstoreOrder;
      links: string[];
    }): Promise<boolean> => {
      try {
        // Fresh copy: never overwrite links added since the check.
        const { data } = await agent.InstoreOrders.detail(order.id);
        const fresh = data as InstoreOrder;
        const existing = splitTrackAndTrace(fresh.track_and_trace);
        const toAdd = links.filter((link) => !existing.includes(link));
        if (toAdd.length > 0) {
          await agent.InstoreOrders.update(order.id, {
            order_status: fresh.order_status_name,
            order_code: fresh.order_code,
            order_type_instore_id: fresh.order_type_instore_id,
            order_date: toBackendDate(fresh.order_date),
            instore_type_id: fresh.instore_type_id,
            template_id: fresh.template_id,
            track_and_trace: [...existing, ...toAdd].join(";"),
          });
        }
        return true;
      } catch {
        return false;
      } finally {
        setProgress((p) => ({ ...p, done: p.done + 1 }));
      }
    };

    for (let i = 0; i < plan.length; i += BATCH) {
      if (stopRequested.current) {
        setStopped(true);
        break;
      }
      const batch = plan.slice(i, i + BATCH);
      const results = await Promise.all(batch.map(saveOne));
      for (let j = 0; j < results.length; j++) {
        if (results[j]) updated += 1;
        else failed.push(batch[j].order.order_code);
      }
    }

    setFailedCodes(failed);
    setStep("done");
    if (updated > 0) dispatch(refreshInstoreOrdersAsync());
  };

  const percent =
    progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  //#endregion

  //#region RENDER
  const plural = (n: number, word: string) =>
    `${n} ${word}${n === 1 ? "" : "s"}`;
  const buttonBase =
    "inline-flex items-center gap-x-1 rounded px-3 py-1.5 text-sm font-semibold shadow-sm disabled:cursor-not-allowed disabled:opacity-40";
  const whiteButton = `${buttonBase} bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50`;
  const primaryButton = `${buttonBase} bg-emerald-700 text-white hover:bg-emerald-600 disabled:hover:bg-emerald-700`;

  const progressBar = (label: string) => (
    <div className="mt-6">
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
        <div
          className="h-full rounded-full bg-emerald-600 transition-all"
          style={{ width: `${percent}%` }}
        />
      </div>
      <p className="mt-2 text-center text-sm font-semibold text-gray-900">
        {progress.done} / {progress.total} {label}
      </p>
    </div>
  );

  return (
    <div className="flex items-center">
      <button
        type="button"
        onClick={open}
        title="Import track & trace links from Excel"
        className="inline-flex items-center gap-x-1 rounded bg-white px-2 py-1 text-sm font-semibold text-emerald-900 shadow-sm ring-1 ring-inset ring-emerald-300 hover:bg-emerald-50"
      >
        <ArrowUpTrayIcon className="h-4 w-4" aria-hidden="true" />
        Import track &amp; trace
      </button>

      <MainModal
        type={IMPORT_TRACK_AND_TRACE_MODAL}
        closeHandler={close}
        closeDisabled={busy}
        maxWidthClassName="sm:max-w-4xl"
      >
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            Import track &amp; trace
          </h3>

          {/* 1. PICK */}
          {step === "pick" && (
            <div className="mt-4">
              <p className="text-sm text-gray-500">
                Excel or CSV with the order code and the track &amp; trace link,
                one row per parcel.
              </p>
              <div className="mt-4 flex items-center gap-3">
                <input
                  ref={fileInput}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => {
                    pickFile(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  className={primaryButton}
                >
                  <ArrowUpTrayIcon className="h-4 w-4" aria-hidden="true" />
                  Choose file
                </button>
                <button
                  type="button"
                  onClick={downloadTrackAndTraceTemplate}
                  className="text-sm font-semibold text-emerald-800 hover:text-emerald-600"
                >
                  Download template
                </button>
              </div>
            </div>
          )}

          {/* 2. CHECKING */}
          {step === "checking" && (
            <div className="mt-4">
              <p className="text-sm text-gray-500">
                Checking {fileName} against the orders…
              </p>
              {progressBar("orders checked")}
            </div>
          )}

          {/* 3. REVIEW */}
          {step === "review" && (
            <div className="mt-4">
              <p className="text-sm text-gray-500">
                {fileName} · {plural(rows.length, "row")}
              </p>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                {(
                  Object.keys(
                    TRACK_AND_TRACE_STATUS_LABEL,
                  ) as TrackAndTraceRowStatus[]
                )
                  .filter((status) => counts[status])
                  .map((status) => (
                    <span key={status} className={STATUS_CLASS[status]}>
                      <b>{counts[status]}</b>{" "}
                      {TRACK_AND_TRACE_STATUS_LABEL[status]}
                    </span>
                  ))}
              </div>

              <div className="mt-4 max-h-80 overflow-auto rounded-md ring-1 ring-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="sticky top-0 bg-gray-50 text-left font-semibold text-slate-800">
                    <tr>
                      <th className="px-3 py-2">row</th>
                      <th className="px-3 py-2">order code</th>
                      <th className="px-3 py-2">track &amp; trace</th>
                      <th className="px-3 py-2">result</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white text-gray-600">
                    {rows.map((row) => (
                      <tr key={row.rowNumber}>
                        <td className="px-3 py-1.5">{row.rowNumber}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          {row.orderCode || "---"}
                        </td>
                        <td
                          className="px-3 py-1.5 max-w-xs truncate"
                          title={row.link}
                        >
                          {row.link || "---"}
                        </td>
                        <td
                          className={classNames(
                            "px-3 py-1.5 whitespace-nowrap font-medium",
                            STATUS_CLASS[row.status],
                          )}
                        >
                          {TRACK_AND_TRACE_STATUS_LABEL[row.status]}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={reset} className={whiteButton}>
                  Other file
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={plan.length === 0}
                  className={primaryButton}
                >
                  {plan.length === 0
                    ? "Nothing to add"
                    : `Add ${plural(newLinkCount, "link")} to ${plural(plan.length, "order")}`}
                </button>
              </div>
            </div>
          )}

          {/* 4. SAVING */}
          {step === "saving" && (
            <div className="mt-4">
              {progressBar("orders updated")}
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={() => (stopRequested.current = true)}
                  className={whiteButton}
                >
                  Stop
                </button>
              </div>
            </div>
          )}

          {/* 5. DONE */}
          {step === "done" && (
            <div className="mt-4 text-center">
              <p className="text-sm font-semibold text-gray-900">
                {plural(progress.done - failedCodes.length, "order")} updated
              </p>
              {failedCodes.length > 0 && (
                <p className="mt-1 text-sm text-red-600">
                  {failedCodes.length} failed: {failedCodes.join(", ")}
                </p>
              )}
              {stopped && (
                <p className="mt-1 text-xs text-gray-400">
                  Stopped — {plural(progress.total - progress.done, "order")}{" "}
                  not processed.
                </p>
              )}
              <div className="mt-6 flex justify-center">
                <button type="button" onClick={close} className={primaryButton}>
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </MainModal>
    </div>
  );
  //#endregion
}
